/**
 * AI Recruit - Main Application Component
 * Agentic AI-powered recruitment platform frontend with Vite + Tailwind CSS
 * 
 * NO MANUAL RULES - NO FALLBACKS - PURE AI INTELLIGENCE
 */

import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Provider } from 'react-redux'
import { Toaster } from 'react-hot-toast'

import { store } from './stores'
import { useAuth } from './hooks/useAuth'
import Layout from './components/layout/Layout'
import NoSidebarLayout from './components/layout/NoSidebarLayout'
import LoadingSpinner from './components/common/LoadingSpinner'
import IdleTimeoutHandler from './components/common/IdleTimeoutHandler'
import VersionUpdateNotifier from './components/common/VersionUpdateNotifier'
import { IDLE_TIMEOUT_CONFIG } from './config/idleTimeout'
import { BulkApplyProvider } from './contexts/BulkApplyContext'
import { BulkUploadProvider } from './contexts/BulkUploadContext'
import type { Capability } from './config/permissions'
import { ORG_SETTINGS_CAPABILITIES } from './config/permissions'
import { usePermission } from './hooks/usePermission'

// Authentication state interface
interface AuthProps {
  isAuthenticated: boolean
  isLoading: boolean
}

// Pages
import LoginPage from './pages/auth/LoginPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import DashboardPage from './pages/dashboard/DashboardPage'
import ResumesPage from './pages/resumes/ResumesPage'
import ResumeDetailPage from './pages/resumes/ResumeDetailPage'
import ResumeComparePage from './pages/resumes/ResumeComparePage'
import JobsPage from './pages/jobs/V2/JobsPage'
import JobDetailPage from './pages/jobs/V2/JobDetailPage'
import ApplyMatchingPage from './pages/matching/ApplyMatchingPage'
import TeamConfigurationPage from './pages/matching/TeamConfigurationPage'
import InterviewsPage from './pages/interviews/InterviewsPage'
import InterviewConductPage from './pages/interviews/InterviewConductPage'
import InterviewRecordingPage from './pages/interviews/InterviewRecordingPage'
import InterviewResultsPage from './pages/interviews/InterviewResultsPage'
import AnalyticsPage from './pages/analytics/AnalyticsPage'
import SettingsPage from './pages/settings/SettingsPage'
import OrganizationSettingsPage from './pages/settings/OrganizationSettingsPage'
import { OrgSettingsLayout } from './pages/settings/OrgSettingsLayout'
import TeamAnalyticsPage from './pages/team-analytics/TeamAnalyticsPage'
import PublicInterviewPage from './pages/publicInterview/PublicInterviewPage'
import ScorecardPage from './pages/proctoredInterview/ScorecardPage'
import PreJoinPage from './pages/liveInterview/PreJoinPage'
import LiveInterviewRoomPage from './pages/liveInterview/LiveInterviewRoomPage'
import RecruiterRoomPage from './pages/liveInterview/RecruiterRoomPage'
import LiveInterviewSummaryPage from './pages/liveInterview/SummaryPage'
import AcknowledgementPage from './pages/public/AcknowledgementPage'

// Lazy import UsersPage (to be added)
const UsersPage = React.lazy(() => import('@/pages/settings/UsersPage'))

const SourceManagementPage = React.lazy(() => import('@/pages/settings/SourceManagementPage'))
const QuestionTypeManagementPage = React.lazy(() => import('@/pages/settings/QuestionTypeManagementPage'))
const RolesPermissionsPage = React.lazy(() => import('@/pages/settings/RolesPermissionsPage'))
const RoleDetailPage = React.lazy(() => import('@/pages/settings/RoleDetailPage'))
const AuditLogPage = React.lazy(() => import('@/pages/settings/AuditLogPage'))

// Create React Query client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
})

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode } & AuthProps> = ({ 
  children, 
  isAuthenticated, 
  isLoading 
}) => {
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-gray-600">Loading your workspace...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  return <Layout>{children}</Layout>
}

// Capability arrays shared between a route's own RoleRoute guard and
// LANDING_CANDIDATES below (bug fix, 2026-08-25) — extracted to a single
// named constant per multi-capability route so the two can never drift
// apart the way two independently-typed copies of the same array could.
// Single-capability routes below are still restated inline (their own
// RoleRoute prop is one short string literal, low risk of transcription
// drift), but these three each have several long, similar-looking keys.
const TEAMS_CAPABILITIES: Capability[] = ['teams.viewOwn', 'teams.create', 'teams.rename', 'teams.reassignManager', 'teams.delete']
const USERS_CAPABILITIES: Capability[] = ['users.create', 'users.edit', 'users.delete', 'users.resetPasswordDefault', 'users.setCustomPassword', 'users.view', 'users.viewAll']
const SOURCES_CAPABILITIES: Capability[] = ['sources.createJobSource', 'sources.editJobSource', 'sources.activateDeactivateJobSource', 'sources.createResumeSource', 'sources.editResumeSource', 'sources.activateDeactivateResumeSource', 'sources.viewJobSources', 'sources.viewResumeSources']

// Ordered list of "real module home pages" a signed-in user could land on,
// most-preferred first (mirrors Sidebar.tsx's nav order, then the
// Organization Settings routes) — bug fix, 2026-08-25. Used by
// resolveLandingPath() below to replace every hardcoded `/dashboard`
// fallback (RoleRoute's denial branch, the "/" route, and PublicRoute's
// post-login default) with "the first thing this role can actually see."
// Each entry's `capability` MUST match that same path's own RoleRoute
// `capability` prop exactly, verbatim, below — cross-checked by hand
// against every RoleRoute usage in this file when this was added; a
// mismatch here would silently recreate the same redirect-loop bug this
// fix exists to close.
const LANDING_CANDIDATES: { path: string; capability: Capability | Capability[] }[] = [
  { path: '/dashboard', capability: 'dashboard.view' },
  { path: '/resumes', capability: 'resumes.viewList' },
  { path: '/jobs', capability: 'jobs.viewList' },
  { path: '/matching', capability: 'matching.view' },
  { path: '/team-analytics', capability: 'analytics.teamDashboard' },
  { path: '/matching/teams', capability: TEAMS_CAPABILITIES },
  { path: '/admin/users', capability: USERS_CAPABILITIES },
  { path: '/admin/sources', capability: SOURCES_CAPABILITIES },
  { path: '/org-settings', capability: ORG_SETTINGS_CAPABILITIES },
  { path: '/admin/roles', capability: 'roles.viewList' },
]

function resolveLandingPath(can: (capability: Capability) => boolean): string | null {
  for (const { path, capability } of LANDING_CANDIDATES) {
    const required = Array.isArray(capability) ? capability : [capability]
    if (required.some((c) => can(c))) return path
  }
  return null
}

// Shown instead of a blank screen when resolveLandingPath finds nothing —
// a role holding zero of the capabilities above (bug fix, 2026-08-25).
const NoAccessMessage: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
      <div className="text-gray-400 mb-4">
        <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h1 className="text-xl font-semibold text-gray-900 mb-2">No access</h1>
      <p className="text-gray-600">
        You don't have access to any part of this app. Contact your administrator to have a role or permissions assigned to your account.
      </p>
    </div>
  </div>
)

// Resolves a signed-in user's real landing page dynamically instead of a
// hardcoded "/dashboard" (bug fix, 2026-08-25) — used both for the "/"
// route and as RoleRoute's denial fallback below, so a role without
// dashboard.view can no longer redirect-loop into a blank screen.
const DefaultLandingRoute: React.FC<AuthProps> = ({ isAuthenticated, isLoading }) => {
  const { can, capabilitiesLoading } = usePermission()
  if (isLoading || (isAuthenticated && capabilitiesLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-gray-600">Loading your workspace...</p>
        </div>
      </div>
    )
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  const target = resolveLandingPath(can)
  if (!target) {
    return <NoAccessMessage />
  }
  return <Navigate to={target} replace />
}

// RoleRoute — single generalized capability-gated route guard, replacing
// AdminRoute/ManagerRoute/AdminOrManagerRoute (RBAC_UI_IMPLEMENTATION_PLAN.md §5.4).
// Rewritten 2026-08-14 (AI_RECRUIT_DYNAMIC_RBAC_MASTER_PLAN.md §10.1) off
// the old minRole/exactRoles hierarchy split onto a single `capability`
// check — the escape hatch `exactRoles` existed for analytics.teamDashboard
// turned out to be unnecessary under capabilities: it's seeded as a
// non-hierarchical exact grant, so a plain `can()` check reproduces the
// same exactness for free, no separate mechanism needed. (roles.manage,
// this comment's other original example, was retired 2026-08-31 and
// replaced with six ordinary hierarchical keys — see the routes below.)
// `noSidebar` renders NoSidebarLayout instead of Layout, for pages that
// supply their own top-level nav (e.g. /org-settings).
const RoleRoute: React.FC<
  { children: React.ReactNode; capability: Capability | Capability[]; noSidebar?: boolean } & AuthProps
> = ({ children, isAuthenticated, isLoading, capability, noSidebar }) => {
  const { can, capabilitiesLoading } = usePermission()
  if (isLoading || (isAuthenticated && capabilitiesLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-gray-600">Loading your workspace...</p>
        </div>
      </div>
    )
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  // Array form is OR semantics — admits any role holding at least one of the
  // listed capabilities. Added 2026-08-16 (§25) for routes whose content is
  // split across several independently-gated sub-sections (e.g. /org-settings),
  // where a single required capability would lock out a role holding every
  // real sub-capability except that one.
  const required = Array.isArray(capability) ? capability : [capability]
  if (!required.some((c) => can(c))) {
    // Was a hardcoded `<Navigate to="/dashboard" replace />` — a role
    // without dashboard.view denied here would redirect to /dashboard,
    // get denied there too, and loop back to itself forever (blank white
    // screen, bug fix 2026-08-25). Resolves to wherever this role
    // actually has access instead.
    return <DefaultLandingRoute isAuthenticated={isAuthenticated} isLoading={isLoading} />
  }
  return noSidebar ? <NoSidebarLayout>{children}</NoSidebarLayout> : <Layout>{children}</Layout>
}

// Public Route component (for login page)
const PublicRoute: React.FC<{ children: React.ReactNode } & AuthProps> = ({ 
  children, 
  isAuthenticated, 
  isLoading 
}) => {
  const location = useLocation()
  // Was '/dashboard' — a role without dashboard.view landing here with no
  // explicit `from` (e.g. a fresh /login visit) would hit the same
  // hardcoded-fallback loop RoleRoute had (bug fix, 2026-08-25). '/' now
  // resolves dynamically via DefaultLandingRoute below instead.
  const from = (location.state as { from?: string })?.from || '/'

  console.log('PublicRoute - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading) // Debug log
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-gray-600">Checking authentication...</p>
        </div>
      </div>
    )
  }

  if (isAuthenticated) {
    console.log('PublicRoute redirecting to', from) // Debug log
    return <Navigate to={from} replace />
  }

  return <>{children}</>
}

// Error Boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Error Boundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
            <div className="text-error-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-4">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            {this.state.error && this.state.error.message && (
              <p className="text-xs text-gray-400 mb-4 break-words">
                {this.state.error.message}
              </p>
            )}
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// 404 Not Found component
const NotFoundPage: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="max-w-md w-full text-center">
      <div className="text-gray-400 mb-8">
        <svg className="w-24 h-24 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m6 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
      <h2 className="text-2xl font-semibold text-gray-700 mb-4">Page Not Found</h2>
      <p className="text-gray-600 mb-8">
        The page you're looking for doesn't exist or has been moved.
      </p>
      <div className="space-x-4">
        <button
          onClick={() => window.history.back()}
          className="btn-outline"
        >
          Go Back
        </button>
        <Link to="/dashboard" className="btn-primary">Go to Dashboard</Link>
      </div>
    </div>
  </div>
)

// Redirects an old /stage-acknowledgement/:token link (already sent in
// earlier emails, before the acknowledgement page's route was renamed to
// /acknowledge/:token) to the current route, preserving the token.
const LegacyAcknowledgementRedirect: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  return <Navigate to={`/acknowledge/${token ?? ''}`} replace />
}

// App Routes component that handles authentication state
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      <Route 
        path="/auth/login" 
        element={
          <PublicRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      <Route 
        path="/auth/forgot-password" 
        element={
          <PublicRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <ForgotPasswordPage />
          </PublicRoute>
        } 
      />
      
      <Route 
        path="/auth/reset-password" 
        element={
          <PublicRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <ResetPasswordPage />
          </PublicRoute>
        } 
      />

      {/* Public candidate-facing proctored interview — no auth, token in the
          URL is the credential. Not wrapped in PublicRoute (which redirects
          an already-logged-in user away) since an interviewer could open
          this link while signed in to preview it. */}
      <Route path="/interview/public/:token" element={<PublicInterviewPage />} />

      {/* Public candidate-facing Live Interview pre-join gate — same
          no-auth, token-in-URL pattern as PublicInterviewPage above. Not
          wrapped in PublicRoute (which redirects an already-logged-in user
          away) since an interviewer could open this link while signed in
          to preview it. */}
      <Route path="/live-interview/attend/:token" element={<PreJoinPage />} />

      {/* Public candidate-facing Live Interview room — same no-auth,
          token-in-URL pattern as PreJoinPage above. Reached via PreJoinPage's
          navigate() on Join; not wrapped in PublicRoute for the same reason. */}
      <Route path="/live-interview/room/:token" element={<LiveInterviewRoomPage />} />

      {/* Public candidate-facing stage acknowledgement page — same no-auth,
          token-in-URL pattern as the routes above. Reached from the
          acknowledgement mail sent via StageAcknowledgementPanel (Apply
          Matching tab). Not wrapped in PublicRoute for the same reason as
          PublicInterviewPage: a recruiter could open the link while signed
          in to preview it. */}
      <Route path="/acknowledge/:token" element={<AcknowledgementPage />} />

      {/* Back-compat: an earlier build of the acknowledgement email builder
          linked here (/stage-acknowledgement/:token) before it was aligned
          to the route above. Any already-sent email still carries that old
          path, so keep redirecting it rather than breaking those links. */}
      <Route
        path="/stage-acknowledgement/:token"
        element={<LegacyAcknowledgementRedirect />}
      />

      <Route
        path="/proctored-interview/scorecard/:sessionId"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <ScorecardPage />
          </ProtectedRoute>
        }
      />

      {/* Recruiter-facing Live Interview summary — authenticated, normal app
          route (RoleRoute + Layout), unlike the public token-based
          attend/room routes above. Reached from LiveInterviewTab's
          "View Summary" link once a session's status is "completed". */}
      <Route
        path="/live-interview/sessions/:sessionId/summary"
        element={
          <RoleRoute capability="liveInterview.view" isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <LiveInterviewSummaryPage />
          </RoleRoute>
        }
      />

      {/* Recruiter-facing Live Interview room — authenticated, id-addressed
          (not token-addressed like the candidate's public room above). Fixes
          a Phase A leftover: LiveInterviewTab's "Join Now" used to navigate
          recruiters into the public /live-interview/room/:token route using
          the candidate's own access_token as if it were a recruiter
          credential, because this page didn't exist yet (see
          RecruiterRoomPage.tsx's own header comment). */}
      <Route
        path="/live-interview/sessions/:sessionId/room"
        element={
          <RoleRoute capability="liveInterview.manage" noSidebar isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <RecruiterRoomPage />
          </RoleRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <RoleRoute capability="dashboard.view" isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <DashboardPage />
          </RoleRoute>
        }
      />

      <Route
        path="/resumes"
        element={
          <RoleRoute capability="resumes.viewList" isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <ResumesPage />
          </RoleRoute>
        }
      />

      <Route
        path="/resumes/:id"
        element={
          <RoleRoute capability="resumes.viewDetails" isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <ResumeDetailPage />
          </RoleRoute>
        }
      />

      <Route
        path="/resumes/compare"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <ResumeComparePage />
          </ProtectedRoute>
        } 
      />
      
      <Route
        path="/jobs"
        element={
          <RoleRoute capability="jobs.viewList" isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <JobsPage />
          </RoleRoute>
        }
      />

      <Route
        path="/jobs/:id"
        element={
          <RoleRoute capability="jobs.viewDetails" isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <JobDetailPage />
          </RoleRoute>
        }
      />

      <Route
        path="/matching"
        element={
          <RoleRoute capability="matching.view" isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <ApplyMatchingPage />
          </RoleRoute>
        }
      />

      <Route
        path="/matching/teams"
        element={
          <RoleRoute capability={TEAMS_CAPABILITIES} isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <TeamConfigurationPage />
          </RoleRoute>
        }
      />

      {/* Interview feature hidden per request — its only UI entry point (the
          Interview tab on Job/Candidate detail pages) is commented out, but
          these routes stayed reachable by anyone who typed/pasted the URL
          directly since ProtectedRoute only checks login, not a capability.
          Commenting the routes out too so the path itself is gone (unmatched
          route falls through to the catch-all), not just unlinked.*/}
      <Route
        path="/interviews"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <InterviewsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/interviews/:sessionId/conduct"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <InterviewConductPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/interviews/:interviewId/record"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <InterviewRecordingPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/interviews/:interviewId/results"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <InterviewResultsPage />
          </ProtectedRoute>
        }
      />
      

      <Route
        path="/admin/users"
        element={
          <RoleRoute capability={USERS_CAPABILITIES} isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <React.Suspense fallback={<div className="p-6"><LoadingSpinner /></div>}>
              <UsersPage />
            </React.Suspense>
          </RoleRoute>
        }
      />

      <Route
        path="/admin/sources"
        element={
          <RoleRoute capability={SOURCES_CAPABILITIES} isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <React.Suspense fallback={<div className="p-6"><LoadingSpinner /></div>}>
              <SourceManagementPage />
            </React.Suspense>
          </RoleRoute>
        }
      />

      {/* Question Type Management — ProtectedRoute (login only), not RoleRoute:
          questionTypes.view/manage aren't in the RBAC capability catalog yet
          (new feature), and RoleRoute has no legacy-role fallback the way the
          backend's gated() does, so a RoleRoute here would lock out every
          role until that catalog is seeded. The page itself still gates its
          create/edit controls by role (see QuestionTypeManagementPage). */}
      <Route
        path="/admin/question-types"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <React.Suspense fallback={<div className="p-6"><LoadingSpinner /></div>}>
              <QuestionTypeManagementPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* capability="roles.manage" is naturally exact already — no grant row
          exists at all (AI_RECRUIT_DYNAMIC_RBAC_MASTER_PLAN.md §5.B/§8), so
          `can()` resolves it to "whichever role currently holds
          is_bypass_all" for free, same as the old exactRoles={['admin']}
          escape hatch used to encode by hand. */}
      {/* noSidebar + OrgSettingsLayout (2026-08-21) — these three routes are
          only ever reached from within Organization Settings' "Roles &
          Permissions" tab, so they render inside the same left-nav shell
          instead of the main app sidebar; otherwise the sidebar visibly
          swapped when navigating from the list to Edit/Add Role. */}
      <Route
        path="/admin/roles"
        element={
          <RoleRoute capability="roles.viewList" noSidebar isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <OrgSettingsLayout activeSection="roles">
              <React.Suspense fallback={<div className="p-6"><LoadingSpinner /></div>}>
                <RolesPermissionsPage />
              </React.Suspense>
            </OrgSettingsLayout>
          </RoleRoute>
        }
      />

      {/* Full-page role create/edit (2026-08-16) — replaces the former
          modal. The static "/new" segment is matched before the dynamic
          ":roleId" one by react-router regardless of declaration order. */}
      <Route
        path="/admin/roles/new"
        element={
          <RoleRoute capability="roles.create" noSidebar isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <OrgSettingsLayout activeSection="roles">
              <React.Suspense fallback={<div className="p-6"><LoadingSpinner /></div>}>
                <RoleDetailPage mode="create" />
              </React.Suspense>
            </OrgSettingsLayout>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/roles/:roleId"
        element={
          <RoleRoute capability="roles.viewList" noSidebar isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <OrgSettingsLayout activeSection="roles">
              <React.Suspense fallback={<div className="p-6"><LoadingSpinner /></div>}>
                <RoleDetailPage mode="edit" />
              </React.Suspense>
            </OrgSettingsLayout>
          </RoleRoute>
        }
      />
      <Route
        path="/admin/audit-log"
        element={
          <RoleRoute capability="roles.viewAuditLog" noSidebar isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <OrgSettingsLayout activeSection="roles">
              <React.Suspense fallback={<div className="p-6"><LoadingSpinner /></div>}>
                <AuditLogPage />
              </React.Suspense>
            </OrgSettingsLayout>
          </RoleRoute>
        }
      />


      {/* capability="analytics.teamDashboard" is also naturally exact
          already — seeded to exactly `manager`, not `manager`+
          (5.6.A's closure) — same reasoning as /admin/roles above. */}
      <Route
        path="/team-analytics"
        element={
          <RoleRoute capability="analytics.teamDashboard" isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <TeamAnalyticsPage />
          </RoleRoute>
        }
      />

      <Route
        path="/org-settings"
        element={
          <RoleRoute capability={ORG_SETTINGS_CAPABILITIES} noSidebar isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <OrganizationSettingsPage />
          </RoleRoute>
        }
      />

      <Route
        path="/analytics"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />

      {/* Default redirect */}
      <Route
        path="/"
        element={<DefaultLandingRoute isAuthenticated={isAuthenticated} isLoading={isLoading} />}
      />
      
      {/* 404 fallback */}
      <Route 
        path="*" 
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <NotFoundPage />
          </ProtectedRoute>
        } 
      />
    </Routes>
  )
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <Router>
            {/* Inside Router (DuplicateDetectionModal uses useNavigate) and
                outside the routes, so a running bulk apply/upload survives navigation. */}
            <BulkApplyProvider>
              <BulkUploadProvider>
                <div className="min-h-screen bg-gray-50">
                  <AppRoutes />
                </div>
              </BulkUploadProvider>
            </BulkApplyProvider>
          </Router>
          
          {/* Global Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#ffffff',
                color: '#1f2937',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#ffffff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#ffffff',
                },
              },
            }}
          />
          
          {/* React Query DevTools - Only in development */}
          {import.meta.env.DEV && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
          
          {/* Idle Timeout Handler - Only when authenticated */}
          <IdleTimeoutHandler
            timeoutMinutes={IDLE_TIMEOUT_CONFIG.TIMEOUT_MINUTES}
            warningMinutes={IDLE_TIMEOUT_CONFIG.WARNING_MINUTES}
          />

          {/* Notifies the user (with a manual refresh button) when a new build has been deployed */}
          <VersionUpdateNotifier />
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  )
}

export default App